import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {
  apiCreateFdApplicationDoc,
  apiDeleteFdApplicationDoc,
  apiUpdateFdApplicationProgress,
  apiUploadFile,
} from 'constants/Api';
import {_ToTitleCase} from 'constants/index';
import type {
  IFdApplicationDocType,
  IFdApplicationType,
} from 'constants/interfaces';

import ApplicationJourneyStepLayout from './ApplicationJourneyStepLayout';
import {useSupplierApplicationJourney} from './SupplierApplicationJourneyContext';
import {ErrorBanner} from './shared/components';
import DocumentCategoryCard from './Documents/DocumentCategoryCard';
import DocumentPreviewModal from './Documents/DocumentPreviewModal';
import DocumentUploadModal from './Documents/DocumentUploadModal';
import {
  DOCUMENT_REQUIREMENTS,
  adaptApplicationDocumentRecord,
  getGroupedDocumentRequirements,
  getRequirementById,
  type DocumentRequirement,
  type DocumentUploadFile,
} from './Documents/documentDefinitions';

const DOCUMENTS_PROGRESS_STEP = 7;

function prettyApplicationState(value: unknown) {
  return _ToTitleCase(
    String(value || 'IN_PROGRESS')
      .replace(/_/g, ' ')
      .toLowerCase(),
  );
}

function buildFilesByRequirement(files: DocumentUploadFile[]) {
  return files.reduce<Record<string, DocumentUploadFile[]>>((acc, file) => {
    if (!acc[file.requirementId]) acc[file.requirementId] = [];
    acc[file.requirementId].push(file);
    return acc;
  }, {});
}

const ApplicationDocumentsStep: React.FC = () => {
  const {
    application,
    applicationId,
    goNext,
    goPrevious,
    refreshJourney,
    setApplication,
  } = useSupplierApplicationJourney();

  const currentApplication = application as IFdApplicationType | undefined;
  const numericApplicationId = Number(
    currentApplication?.id || applicationId || 0,
  );
  const groupedRequirements = useMemo(
    () => getGroupedDocumentRequirements(),
    [],
  );

  const [files, setFiles] = useState<DocumentUploadFile[]>([]);
  const [activeRequirement, setActiveRequirement] =
    useState<DocumentRequirement | null>(null);
  const [previewRequirementId, setPreviewRequirementId] = useState<
    string | null
  >(null);
  const [previewFileUid, setPreviewFileUid] = useState<string | null>(null);
  const [previewRemoving, setPreviewRemoving] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const existingFiles = (currentApplication?.applicationDocuments || []).map(
      adaptApplicationDocumentRecord,
    );

    setFiles((current) => {
      const localPending = current.filter((file) => !file.id);
      const next = [...existingFiles];

      localPending.forEach((file) => {
        if (!next.some((existing) => existing.uid === file.uid)) {
          next.push(file);
        }
      });

      return next;
    });
  }, [currentApplication?.applicationDocuments]);

  const filesByRequirement = useMemo(
    () => buildFilesByRequirement(files),
    [files],
  );

  const requiredMissing = useMemo(() => {
    return DOCUMENT_REQUIREMENTS.filter(
      (requirement) => requirement.isRequired,
    ).filter(
      (requirement) =>
        !(filesByRequirement[requirement.id] || []).some(
          (file) => file.status === 'uploaded',
        ),
    );
  }, [filesByRequirement]);

  const hasUploading = files.some((file) => file.status === 'uploading');
  const uploadedFilesCount = files.filter(
    (file) => file.status === 'uploaded',
  ).length;
  const uploadedCategoryCount = useMemo(
    () =>
      groupedRequirements.filter((group) =>
        group.requirements.some((requirement) =>
          (filesByRequirement[requirement.id] || []).some(
            (file) => file.status === 'uploaded',
          ),
        ),
      ).length,
    [filesByRequirement, groupedRequirements],
  );

  const previewFiles = useMemo(() => {
    if (!previewRequirementId) return [] as DocumentUploadFile[];
    return (filesByRequirement[previewRequirementId] || []).filter(
      (file) => file.status === 'uploaded',
    );
  }, [filesByRequirement, previewRequirementId]);

  const previewRequirement = useMemo(
    () =>
      previewRequirementId
        ? getRequirementById(previewRequirementId)
        : undefined,
    [previewRequirementId],
  );

  const previewSectionName = useMemo(() => {
    if (!previewRequirement?.categoryId) return null;
    return (
      groupedRequirements.find(
        (group) => group.category.id === previewRequirement.categoryId,
      )?.category.name ?? null
    );
  }, [groupedRequirements, previewRequirement?.categoryId]);

  const previewIndex = useMemo(() => {
    if (!previewFileUid) return -1;
    return previewFiles.findIndex((file) => file.uid === previewFileUid);
  }, [previewFileUid, previewFiles]);

  const currentPreviewFile = useMemo(() => {
    if (previewFiles.length === 0) return null;
    if (previewIndex >= 0) return previewFiles[previewIndex];
    return previewFiles[0];
  }, [previewFiles, previewIndex]);

  useEffect(() => {
    if (!previewRequirementId) return;

    if (previewFiles.length === 0) {
      setPreviewRequirementId(null);
      setPreviewFileUid(null);
      return;
    }

    if (!previewFileUid) {
      setPreviewFileUid(previewFiles[0].uid);
      return;
    }

    const exists = previewFiles.some((file) => file.uid === previewFileUid);
    if (!exists) {
      setPreviewFileUid(previewFiles[0].uid);
    }
  }, [previewFileUid, previewFiles, previewRequirementId]);

  const handleOpenRequirement = useCallback(
    (requirement: DocumentRequirement) => {
      setErrorMessage(null);
      setActiveRequirement(requirement);
    },
    [],
  );

  const handleCloseRequirement = useCallback(() => {
    if (hasUploading) return;
    setActiveRequirement(null);
  }, [hasUploading]);

  const handleOpenPreview = useCallback(
    (requirementId: string, file: DocumentUploadFile) => {
      setPreviewRequirementId(requirementId);
      setPreviewFileUid(file.uid);
    },
    [],
  );

  const handleClosePreview = useCallback(() => {
    setPreviewRequirementId(null);
    setPreviewFileUid(null);
  }, []);

  const handleDownload = useCallback((file: DocumentUploadFile) => {
    if (!file.url) return;
    const nextWindow = window.open(file.url, '_blank', 'noopener,noreferrer');
    if (nextWindow) {
      nextWindow.opener = null;
    }
  }, []);

  const handleRemove = useCallback(
    async (file: DocumentUploadFile) => {
      if (file.id) {
        try {
          await apiDeleteFdApplicationDoc(Number(file.id));
          setFiles((current) =>
            current.filter((item) => item.uid !== file.uid),
          );
          setApplication((current) => {
            if (!current) return current;

            return {
              ...current,
              applicationDocuments: (current.applicationDocuments || []).filter(
                (document) => Number(document.id) !== Number(file.id),
              ),
            };
          });
          setStatusMessage(`Removed ${file.name}`);
          setErrorMessage(null);
        } catch (error: any) {
          setErrorMessage(error?.message || 'Delete failed');
        }

        return;
      }

      setFiles((current) => current.filter((item) => item.uid !== file.uid));
    },
    [setApplication],
  );

  const handlePreviewPrevious = useCallback(() => {
    if (previewFiles.length <= 1) return;
    const length = previewFiles.length;
    const nextIndex = previewIndex > 0 ? previewIndex - 1 : length - 1;
    setPreviewFileUid(previewFiles[nextIndex]?.uid ?? null);
  }, [previewFiles, previewIndex]);

  const handlePreviewNext = useCallback(() => {
    if (previewFiles.length <= 1) return;
    const length = previewFiles.length;
    const nextIndex = previewIndex >= 0 ? (previewIndex + 1) % length : 0;
    setPreviewFileUid(previewFiles[nextIndex]?.uid ?? null);
  }, [previewFiles, previewIndex]);

  const handlePreviewRemove = useCallback(
    async (file: DocumentUploadFile) => {
      const fallbackUid =
        previewFiles[previewIndex + 1]?.uid ??
        previewFiles[previewIndex - 1]?.uid ??
        null;

      try {
        setPreviewRemoving(true);
        await handleRemove(file);
        setPreviewFileUid(fallbackUid);
      } finally {
        setPreviewRemoving(false);
      }
    },
    [handleRemove, previewFiles, previewIndex],
  );

  const handleUploadFiles = useCallback(
    async (selectedFiles: File[]) => {
      if (!activeRequirement || !numericApplicationId) return;

      setErrorMessage(null);

      for (const file of selectedFiles) {
        const uid = `${activeRequirement.id}_${Date.now()}_${Math.random()
          .toString(36)
          .slice(2, 8)}`;
        const extension = file.name.split('.').pop()?.toLowerCase();
        const now = new Date().toISOString();

        setFiles((current) => [
          ...current,
          {
            uid,
            requirementId: activeRequirement.id,
            name: file.name,
            uploadedAt: now,
            status: 'uploading',
            progress: 1,
            size: file.size,
            extension,
            documentType: activeRequirement.uploadDocumentType,
          },
        ]);

        try {
          const fileKey = `fd-application-docs/${numericApplicationId}_${
            activeRequirement.uploadDocumentType
          }_${Date.now()}.${extension || 'pdf'}`;
          const url = await apiUploadFile(file, fileKey);

          setFiles((current) =>
            current.map((item) =>
              item.uid === uid ? {...item, progress: 85} : item,
            ),
          );

          const record = await apiCreateFdApplicationDoc({
            applicationId: numericApplicationId,
            documentType: activeRequirement.uploadDocumentType,
            filePath: url,
          });

          const uploadedAt =
            record.updatedAt || record.createdAt || new Date().toISOString();
          const uploadedFile: DocumentUploadFile = {
            id: Number(record.id),
            uid: `record-${record.id}`,
            requirementId: activeRequirement.id,
            name: file.name,
            url,
            uploadedAt,
            status: 'uploaded',
            progress: 100,
            size: file.size,
            extension,
            documentType: activeRequirement.uploadDocumentType,
          };

          setFiles((current) =>
            current.map((item) => (item.uid === uid ? uploadedFile : item)),
          );
          setApplication((current) => {
            if (!current) return current;

            const nextRecord: IFdApplicationDocType = {
              id: String(record.id),
              documentType: record.documentType,
              filePath: url,
              createdAt: record.createdAt || uploadedAt,
              updatedAt: record.updatedAt || uploadedAt,
              application: current as IFdApplicationType,
            };

            return {
              ...current,
              applicationDocuments: [
                ...(current.applicationDocuments || []),
                nextRecord,
              ],
            };
          });
          setStatusMessage(`Uploaded ${file.name}`);
        } catch (error: any) {
          setFiles((current) =>
            current.map((item) =>
              item.uid === uid
                ? {
                    ...item,
                    status: 'failed',
                    progress: 100,
                    error: error?.message || 'Upload failed',
                  }
                : item,
            ),
          );
          setErrorMessage(error?.message || 'Upload failed');
          setStatusMessage(`Upload failed for ${file.name}`);
        }
      }
    },
    [activeRequirement, numericApplicationId, setApplication],
  );

  const handleSubmit = useCallback(async () => {
    if (!numericApplicationId || isSubmitting) return;

    if (hasUploading) {
      setErrorMessage('Please wait for uploads to finish.');
      return;
    }

    setErrorMessage(null);
    setIsSubmitting(true);

    try {
      const completed = Boolean(currentApplication?.completed);
      const currentProgress = Number(currentApplication?.progress ?? 0);
      const targetProgress = completed
        ? DOCUMENTS_PROGRESS_STEP
        : Number.isFinite(currentProgress) &&
          currentProgress > DOCUMENTS_PROGRESS_STEP
        ? currentProgress
        : DOCUMENTS_PROGRESS_STEP;

      const updated = await apiUpdateFdApplicationProgress({
        applicationId: numericApplicationId,
        completed,
        progress: targetProgress,
        subProgress: 0,
      });

      setApplication((current) => {
        if (!current) return current;

        return {
          ...current,
          completed: updated?.completed ?? completed,
          progress: updated?.progress ?? targetProgress,
          subProgress: updated?.subProgress ?? 0,
          applicationState:
            updated?.applicationState ?? current.applicationState,
        };
      });

      await refreshJourney();
      goNext();
    } catch (error: any) {
      setErrorMessage(error?.message || 'Failed to update progress.');
    } finally {
      setIsSubmitting(false);
    }
  }, [
    currentApplication,
    goNext,
    hasUploading,
    isSubmitting,
    numericApplicationId,
    refreshJourney,
    setApplication,
  ]);

  const primaryDisabled = !numericApplicationId || isSubmitting || hasUploading;
  const outstandingLabel =
    requiredMissing.length > 0
      ? requiredMissing
          .slice(0, 3)
          .map((requirement) =>
            requirement.name.replace(/\s+\(optional\)$/i, ''),
          )
          .join(', ')
      : 'All required documents uploaded';

  return (
    <ApplicationJourneyStepLayout
      title="Supporting documents"
      description="Upload all statutory, financial and operational documents required to finalise your application."
      secondaryAction={{label: 'Back', onClick: goPrevious}}
      primaryAction={{
        label: 'Save & continue',
        onClick: () => {
          void handleSubmit();
        },
        disabled: primaryDisabled,
        loading: isSubmitting,
      }}
    >
      <div className="documents-step documents-step--source">
        <div className="sr-only" role="status" aria-live="polite">
          {statusMessage}
        </div>

        <ErrorBanner message={errorMessage} />

        <div className="documents-step__layout">
          <article className="documents-side-card">
            <p>Uploaded files</p>
            <strong>{uploadedFilesCount}</strong>
            <span>{DOCUMENT_REQUIREMENTS.length} tracked requirements</span>
          </article>

          <article className="documents-side-card">
            <p>Required remaining</p>
            <strong>{requiredMissing.length}</strong>
            <span>{outstandingLabel}</span>
          </article>

          <article className="documents-side-card">
            <p>Sections covered</p>
            <strong>{uploadedCategoryCount}</strong>
            <span>{groupedRequirements.length} document sections</span>
          </article>

          <article className="documents-side-card">
            <p>Application state</p>
            <strong>
              {prettyApplicationState(currentApplication?.applicationState)}
            </strong>
            <span>
              Application {currentApplication?.id || numericApplicationId}
            </span>
          </article>
        </div>

        <div className="financials-documents">
          <div className="financials-documents__grid" aria-live="polite">
            {groupedRequirements.map((group) => (
              <DocumentCategoryCard
                key={group.category.id}
                category={group.category}
                requirements={group.requirements}
                filesByRequirement={filesByRequirement}
                onOpenRequirement={handleOpenRequirement}
                onPreviewFile={handleOpenPreview}
              />
            ))}
          </div>
        </div>

        <DocumentUploadModal
          open={Boolean(activeRequirement)}
          requirement={activeRequirement}
          files={
            activeRequirement
              ? filesByRequirement[activeRequirement.id] || []
              : []
          }
          onClose={handleCloseRequirement}
          onUploadFiles={handleUploadFiles}
          onRemoveFile={(file) => {
            void handleRemove(file);
          }}
          onDownloadFile={handleDownload}
          isUploading={hasUploading}
        />

        <DocumentPreviewModal
          open={Boolean(previewRequirementId && currentPreviewFile)}
          file={currentPreviewFile}
          title={previewSectionName || previewRequirement?.name || 'Document'}
          files={previewFiles}
          activeFileUid={currentPreviewFile?.uid || null}
          onSelectFile={(file) => setPreviewFileUid(file.uid)}
          onPrevious={handlePreviewPrevious}
          onNext={handlePreviewNext}
          hasPrevious={previewFiles.length > 1}
          hasNext={previewFiles.length > 1}
          onClose={handleClosePreview}
          onDownload={handleDownload}
          onRemove={(file) => {
            void handlePreviewRemove(file);
          }}
          removing={previewRemoving}
        />
      </div>
    </ApplicationJourneyStepLayout>
  );
};

export default ApplicationDocumentsStep;
